import { useEffect, useMemo, useState } from 'react';
import {
  createSeaweedType,
  deleteSeaweedType,
  fetchSeaweedTypes,
  updateSeaweedType,
} from '../services/seaweedTypeService';

function useSeaweedTypes() {
  const [seaweedTypes, setSeaweedTypes] = useState([]);
  const [formValues, setFormValues] = useState({ name: '', description: '' });
  const [formErrors, setFormErrors] = useState({});
  const [editingSeaweedType, setEditingSeaweedType] = useState(null);
  const [editValues, setEditValues] = useState({ name: '', description: '' });
  const [editErrors, setEditErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadSeaweedTypes() {
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchSeaweedTypes();

        if (isMounted) {
          setSeaweedTypes(response);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSeaweedTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setSuccessMessage(''), 3500);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  const sortedSeaweedTypes = useMemo(
    () =>
      [...seaweedTypes].sort((first, second) => first.name.localeCompare(second.name)),
    [seaweedTypes]
  );

  function handleFormChange(event) {
    const { name, value } = event.target;
    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setFormErrors({});
    setSubmitError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateSeaweedTypeForm(formValues, seaweedTypes);

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const createdSeaweedType = await createSeaweedType({
        name: formValues.name.trim(),
        description: String(formValues.description || '').trim(),
      });

      setSeaweedTypes((currentSeaweedTypes) => [...currentSeaweedTypes, createdSeaweedType]);
      setFormValues({ name: '', description: '' });
      setSuccessMessage('Seaweed type added successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function openEditModal(seaweedType) {
    setEditingSeaweedType(seaweedType);
    setEditValues({
      name: seaweedType.name,
      description: seaweedType.description || '',
    });
    setEditErrors({});
    setSubmitError('');
  }

  function closeEditModal() {
    setEditingSeaweedType(null);
    setEditValues({ name: '', description: '' });
    setEditErrors({});
  }

  function handleEditChange(event) {
    const { name, value } = event.target;
    setEditValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setEditErrors({});
    setSubmitError('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();

    const nextErrors = validateSeaweedTypeForm(
      editValues,
      seaweedTypes,
      editingSeaweedType?.id
    );

    if (Object.keys(nextErrors).length) {
      setEditErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const updatedSeaweedType = await updateSeaweedType(editingSeaweedType.id, {
        name: editValues.name.trim(),
        description: String(editValues.description || '').trim(),
      });

      setSeaweedTypes((currentSeaweedTypes) =>
        currentSeaweedTypes.map((seaweedType) =>
          seaweedType.id === updatedSeaweedType.id ? updatedSeaweedType : seaweedType
        )
      );
      closeEditModal();
      setSuccessMessage('Seaweed type updated successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(seaweedType) {
    setIsSaving(true);
    setSubmitError('');

    try {
      await deleteSeaweedType(seaweedType.id);
      setSeaweedTypes((currentSeaweedTypes) =>
        currentSeaweedTypes.filter(
          (currentSeaweedType) => currentSeaweedType.id !== seaweedType.id
        )
      );
      setSuccessMessage('Seaweed type deleted successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  return {
    seaweedTypes: sortedSeaweedTypes,
    formValues,
    formErrors,
    editingSeaweedType,
    editValues,
    editErrors,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    handleFormChange,
    handleSubmit,
    openEditModal,
    closeEditModal,
    handleEditChange,
    handleEditSubmit,
    handleDelete,
  };
}

function validateSeaweedTypeForm(values, seaweedTypes = [], currentSeaweedTypeId = null) {
  const errors = {};
  const normalizedName = values.name.trim();

  if (!normalizedName) {
    errors.name = 'Seaweed type is required.';
    return errors;
  }

  const hasDuplicateName = seaweedTypes.some((seaweedType) => {
    if (currentSeaweedTypeId !== null && seaweedType.id === currentSeaweedTypeId) {
      return false;
    }

    return (
      String(seaweedType.name || '').trim().toLowerCase() === normalizedName.toLowerCase()
    );
  });

  if (hasDuplicateName) {
    errors.name = 'This seaweed type already exists.';
  }

  if (String(values.description || '').trim().length > 1000) {
    errors.description = 'Description must be 1000 characters or fewer.';
  }

  return errors;
}

export default useSeaweedTypes;
